import { ChevronDownIcon } from '@radix-ui/react-icons';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

interface IProps {
  children: ReactNode;

  // applied to the outer (positioning) wrapper
  className?: string;
  style?: CSSProperties;

  // applied to the inner scrollable element
  contentClassName?: string;
  contentStyle?: CSSProperties;
}

/**
 * Wraps scrollable content and renders fading top/bottom affordances that
 * appear only when there is more content above/below the current scroll
 * position. Useful on short viewports where OS scrollbars auto-hide and the
 * user has no indication that the area is scrollable.
 */
export const ScrollShadow = (props: IProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ up: false, down: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;

      setScrollState({
        up: scrollTop > 1,
        down: scrollTop < maxScroll - 1,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    // recompute whenever the element or its content changes size
    const ro = new ResizeObserver(update);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));

    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [props.children]);

  return (
    <div
      className={StringHelper.classNames(['ScrollShadow', props.className])}
      style={props.style}
    >
      <div
        ref={scrollRef}
        className={StringHelper.classNames([
          'ScrollShadow__content',
          props.contentClassName,
        ])}
        style={props.contentStyle}
      >
        {props.children}
      </div>

      <div
        className={StringHelper.classNames([
          'ScrollShadow__edge',
          'top',
          scrollState.up ? 'visible' : undefined,
        ])}
      />
      <div
        className={StringHelper.classNames([
          'ScrollShadow__edge',
          'bottom',
          scrollState.down ? 'visible' : undefined,
        ])}
      >
        <ChevronDownIcon />
      </div>
    </div>
  );
};
